/**
 * @format
 */

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSalesSummary } from '../hooks/useSalesSummary';
import { useListedLeadsLogs } from 'features/listed-leads-log/hooks/useListedLeadsLogs';
import { DashboardQueryParams } from '../types/dashboard.types';
import SalesSummary from '../components/SalesSummary';
import SalesSummarySkeleton from '../components/SalesSummarySkeleton';

interface SalesSummaryContainerProps {
  params?: DashboardQueryParams;
}

const SalesSummaryContainer: React.FC<SalesSummaryContainerProps> = ({ params }) => {
  const startDate = useSelector((state: any) => state.dropDown.startDate);
  const endDate = useSelector((state: any) => state.dropDown.endDate);
  const userDetails = useSelector((state: any) => state.user?.userData);
  const selectedOfficeId = useSelector((state: any) => state.dropDown?.officeId);

  // Merge Redux dates with provided params
  const mergedParams = useMemo(() => {
    return {
      ...params,
      startDate,
      endDate,
    };
  }, [params, startDate, endDate]);

  // Build filter for listed leads logs
  const listedLeadsLogFilter = useMemo(() => {
    const filter: any = {};

    if (startDate) filter.startDate = startDate;
    if (endDate) filter.endDate = endDate;

    // Prioritize officeId from params (passed from parent component)
    // Otherwise fall back to role-based filtering
    if (params?.officeId) {
      filter.officeId = params.officeId;
    } else {
      const isRootUser = userDetails?.role === 'root_user';
      if (isRootUser && selectedOfficeId) {
        filter.officeId = selectedOfficeId;
      } else if (userDetails?.role === 'agent' || userDetails?.role === 'admin') {
        if (userDetails?.officeId) {
          filter.officeId = userDetails.officeId;
        }
      }
    }

    return filter;
  }, [startDate, endDate, params?.officeId, userDetails?.role, userDetails?.officeId, selectedOfficeId]);

  const { data, loading, error } = useSalesSummary({ params: mergedParams });
  const { totalCost: listedLeadsLogTotalCost } = useListedLeadsLogs(listedLeadsLogFilter);

  // Combine the marketing cost with listed leads log cost
  const enhancedData = useMemo(() => {
    if (!data) return null;
    return {
      ...data,
      totalCost: (data.totalCost || 0) + (listedLeadsLogTotalCost || 0),
      previousCost: (data.previousCost || 0) + (listedLeadsLogTotalCost || 0),
    };
  }, [data, listedLeadsLogTotalCost]);

  if (loading) {
    return <SalesSummarySkeleton />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!enhancedData) {
    return <div>No data available</div>;
  }

  return <SalesSummary data={enhancedData} />;
};

export default SalesSummaryContainer;
